// Thành phần giao diện dùng chung của Dashboard Telesales (tone hệ thống #3e91ab)
import React from 'react';
import { Tooltip } from 'antd';
import { Info } from 'lucide-react';
import dayjs from 'dayjs';
import { agentOf, consultantOf, type ApptStatus, type Stage } from './data';
import type { AgentStatus } from './logic';

export const PRIMARY = '#3e91ab';

export const Card = ({ title, extra, className = '', children }: { title?: React.ReactNode; extra?: React.ReactNode; className?: string; children: React.ReactNode }) => (
  <div className={`flex min-w-0 flex-col rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-800 ${className}`}>
    {(title || extra) && (
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-[14px] font-bold text-gray-800 dark:text-gray-200">{title}</h3>
        {extra}
      </div>
    )}
    {children}
  </div>
);

/** Biểu tượng (i) giải thích cách tính */
export const Hint = ({ text }: { text: React.ReactNode }) => (
  <Tooltip title={text}><Info size={13} className="shrink-0 cursor-help text-gray-400" /></Tooltip>
);

export type Tone = 'teal' | 'red' | 'amber' | 'green' | 'indigo' | 'sky' | 'slate';
const TONE: Record<Tone, string> = {
  teal: 'bg-[#e8f4f7] text-[#2c6b7f] dark:bg-[#3e91ab]/20 dark:text-[#7cc3d6]',
  red: 'bg-red-50 text-red-600 dark:bg-red-500/15 dark:text-red-400',
  amber: 'bg-amber-50 text-amber-600 dark:bg-amber-500/15 dark:text-amber-400',
  green: 'bg-green-50 text-green-600 dark:bg-green-500/15 dark:text-green-400',
  indigo: 'bg-indigo-50 text-indigo-600 dark:bg-indigo-500/15 dark:text-indigo-400',
  sky: 'bg-sky-50 text-sky-600 dark:bg-sky-500/15 dark:text-sky-400',
  slate: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
};

export function Kpi({ icon, label, value, sub, tone = 'teal', hint, alert, onClick, active }: {
  icon: React.ReactNode; label: string; value: React.ReactNode; sub?: React.ReactNode; tone?: Tone; hint?: React.ReactNode; alert?: boolean; onClick?: () => void; active?: boolean;
}) {
  const cls = `flex h-full min-w-0 flex-col rounded-xl border bg-white p-4 text-left shadow-sm transition dark:bg-gray-800 ${active ? 'border-[#3e91ab] ring-1 ring-[#3e91ab]' : alert ? 'border-red-200 dark:border-red-500/40' : 'border-gray-200 dark:border-gray-700'} ${onClick ? 'hover:border-[#3e91ab] hover:shadow' : ''}`;
  const body = (
    <>
      <span className="flex items-center gap-2 text-[13px] font-semibold text-gray-600 dark:text-gray-300">
        <span className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-lg ${TONE[tone]}`}>{icon}</span>
        <span className="min-w-0 flex-1 truncate">{label}</span>
        {hint && <Hint text={hint} />}
      </span>
      <span className={`mt-2 text-[28px] font-light leading-none ${alert ? 'text-red-500' : 'text-slate-800 dark:text-white'}`}>{value}</span>
      {sub && <span className="mt-1.5 text-xs text-gray-500 dark:text-gray-400">{sub}</span>}
    </>
  );
  return onClick ? <button type="button" onClick={onClick} className={cls}>{body}</button> : <div className={cls}>{body}</div>;
}

/** Thanh tiến độ mảnh */
export const Bar = ({ value, max = 100, color = PRIMARY, className = '' }: { value: number; max?: number; color?: string; className?: string }) => (
  <div className={`h-1.5 w-full overflow-hidden rounded-full bg-gray-100 dark:bg-gray-700 ${className}`}>
    <div className="h-full rounded-full transition-all" style={{ width: `${Math.min(100, max ? (value / max) * 100 : 0)}%`, background: color }} />
  </div>
);

export const Avatar = ({ name, color, size = 26 }: { name: string; color?: string; size?: number }) => (
  <span className="inline-flex shrink-0 items-center justify-center rounded-full font-bold text-white" style={{ width: size, height: size, fontSize: size * 0.42, background: color || '#94a3b8' }}>
    {name.split(' ').pop()?.[0] ?? '?'}
  </span>
);
export const AgentName = ({ id, sub, compact }: { id?: string; sub?: React.ReactNode; compact?: boolean }) => {
  const a = agentOf(id);
  if (!a) return <span className="text-gray-400">—</span>;
  if (compact) return <span className="inline-flex min-w-0 items-center gap-1.5"><Avatar name={a.name} color={a.color} size={22} /><span className="truncate">{a.name}</span></span>;
  return (
    <span className="inline-flex min-w-0 items-center gap-2">
      <Avatar name={a.name} color={a.color} />
      <span className="min-w-0 leading-tight">
        <span className="block truncate font-medium text-gray-800 dark:text-gray-100">{a.name}</span>
        <span className="block text-[11px] text-gray-500">{sub ?? `${a.id} · ${a.mang ?? 'Chưa phân luồng'}`}</span>
      </span>
    </span>
  );
};
export const ConsultantName = ({ id }: { id?: string }) => {
  const c = consultantOf(id);
  if (!c) return <span className="text-gray-400">—</span>;
  return <span className="inline-flex items-center gap-1.5"><Avatar name={c.name} color={c.color} size={22} /><span className="whitespace-nowrap">{c.name}</span></span>;
};

export const AGENT_STATUS: Record<AgentStatus, { label: string; color: string; dot: string }> = {
  calling: { label: 'Đang gọi', color: 'blue', dot: '#3b82f6' },
  acw: { label: 'Hoàn tất sau gọi', color: 'gold', dot: '#f59e0b' },
  ready: { label: 'Sẵn sàng', color: 'green', dot: '#22c55e' },
  break: { label: 'Tạm nghỉ', color: 'default', dot: '#94a3b8' },
  offline: { label: 'Ngoại tuyến', color: 'default', dot: '#cbd5e1' },
};

export const APPT_STATUS: Record<ApptStatus, { label: string; color: string }> = {
  booked: { label: 'Chưa xác nhận', color: 'default' },
  confirmed: { label: 'Đã xác nhận', color: 'blue' },
  arrived: { label: 'Đã đến · chờ tư vấn', color: 'cyan' },
  consulting: { label: 'Đang tư vấn', color: 'processing' },
  won: { label: 'Chốt', color: 'green' },
  deposit: { label: 'Đặt cọc', color: 'lime' },
  no_need: { label: 'Chưa có nhu cầu', color: 'orange' },
  medical: { label: 'Rớt do bệnh lý', color: 'magenta' },
  noshow: { label: 'Không đến', color: 'red' },
  cancelled: { label: 'Đã huỷ', color: 'default' },
};

const STAGE_COLOR: Record<Stage, string> = {
  S0: '#cbd5e1', S1: '#a5d4e1', S2: '#7cc3d6', S3: '#56aec6', S4: '#3e91ab', S5: '#2c6b7f', 'S5.1': '#f59e0b',
  S6: '#1f5566', 'S6.1': '#fb923c', 'S6.2': '#e11d48', CS: '#16a34a',
};
export const stageColor = (s: Stage) => STAGE_COLOR[s];
export const StageChip = ({ s }: { s: Stage }) => (
  <span className="inline-block whitespace-nowrap rounded-full px-2 py-px text-[11px] font-bold text-white" style={{ background: STAGE_COLOR[s] }}>{s}</span>
);

export const hm = (t: number) => dayjs(t).format('HH:mm');
export const dm = (t: number) => dayjs(t).format('DD/MM');
export const num = (n: number) => n.toLocaleString('vi-VN');
/** Tiền rút gọn: 12,5 tr · 1,2 tỷ */
export const money = (v: number) => {
  if (!v) return '0';
  if (v >= 1e9) return `${(v / 1e9).toLocaleString('vi-VN', { maximumFractionDigits: 2 })} tỷ`;
  return `${(v / 1e6).toLocaleString('vi-VN', { maximumFractionDigits: 1 })} tr`;
};
/** mm:ss */
export const mmss = (sec: number) => `${Math.floor(sec / 60)}:${String(Math.round(sec % 60)).padStart(2, '0')}`;
/** Khoảng thời gian dạng "1 giờ 12 phút" / "8 phút" */
export const dur = (ms: number) => {
  const m = Math.max(0, Math.floor(ms / 60000));
  if (m < 60) return `${m} phút`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} giờ${m % 60 ? ` ${m % 60} phút` : ''}`;
  return `${Math.floor(h / 24)} ngày`;
};
/** Hạn xử lý: "Còn 12 phút" hoặc "Trễ 1 giờ 5 phút" */
export const DueText = ({ due, now }: { due: number; now: number }) =>
  due < now
    ? <span className="whitespace-nowrap font-semibold text-red-500">Trễ {dur(now - due)}</span>
    : <span className="whitespace-nowrap text-gray-500">Còn {dur(due - now)}</span>;

export const Empty = ({ text }: { text: string }) => <div className="py-6 text-center text-[13px] text-gray-400">{text}</div>;
